import type { ArrayElement } from '@evidia/shared';
import { List } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Highlighter from 'react-highlight-words';
import { devices, sizes } from '../../const/devices';
import { ROUTES } from '../../const/routes';
import { useActiveSchoolId } from '../../hooks/useActiveSchoolId';
import { useIsTablet } from '../../hooks/useBreakpoint';
import { useHandleClickOutside } from '../../hooks/useHandleClickOutside';
import { mod } from '../../utils/math';
import type { RouterOutput } from '../../utils/trcp';
import { trpc } from '../../utils/trcp';
import EmptyData from '../EmptyData';
import StudentGroup from '../StudentGroup';
import StudentState from '../StudentState';
import { LastUserSearch } from './LastUserSearch';
import UserInputSearch from './UserInputSearch';

type StudentDto = ArrayElement<RouterOutput['student']['students']>;

const Wrapper = styled.div<{ $isTablet: boolean }>`
	display: flex;
	align-items: center;
	width: 100%;
	position: relative;

	.results {
		position: absolute;
		top: ${props => (props.$isTablet ? '45px' : '48px')};
		left: 0;
		width: 535px;
		overflow: hidden;

		z-index: 10000;

		@media ${devices.tablet} {
			width: 100%;
		}
	}

	.ant-list {
		border-top-left-radius: 0px;
		border-top-right-radius: 0px;

		overflow: hidden;
	}

	@media ${devices.mobileL} {
		width: 100%;

		.ant-list {
			border-radius: 0;
			border-top: 0;
		}

		.ant-list-item {
			padding: 20px;
		}

		.ant-input-outlined {
			border-radius: 0;
		}
	}

	/* @media ${devices.tablet} {
		max-width: ${sizes.tablet - 78}px;
	}

	@media ${devices.mobileL} {
		max-width: ${sizes.mobileL - 78}px;
	}

	@media ${devices.mobileM} {
		max-width: ${sizes.mobileM - 78}px;
	}

	@media ${devices.mobileS} {
		max-width: ${sizes.mobileS - 78}px;
	} */
`;

const ListItemLink = styled(Link)`
	.ant-list-item {
		background: red;
	}
	z-index: 10;

	display: flex;
	justify-content: space-between;
	width: 100%;

	cursor: pointer;
`;

const FooterRow = styled.div`
	display: inline-flex;
	align-items: flex-start;
	gap: 15px;
	color: var(--neutral-7, #8c8c8c);
	font-size: 10.069px;
	font-style: normal;
	font-weight: 600;
	line-height: 20.138px;
	letter-spacing: 0.503px;
	text-transform: uppercase;
	padding-top: 5px;

	div {
		gap: 9.15px;
		display: inline-flex;
		align-items: flex-start;
	}

	.border {
		border-radius: 3.661px;
		border-bottom: 2px solid var(--neutral-5, #d9d9d9);
		background: var(--neutral-4, #f0f0f0);
		width: 22.884px;
		height: 22.884px;
		flex-shrink: 0;
	}

	@media ${devices.mobileL} {
		display: none;
	}
`;

export const EmptyText = styled.p`
	color: var(--neutral-13, #000);
	text-align: center;
	font-size: 14px;
	font-style: normal;
	font-weight: bold;
	line-height: 22px;
	padding-top: 1em;
`;

export const ListItem = styled(List.Item)<{ $active: boolean }>`
	background-color: ${({ $active }) => ($active ? '#f0f0f0' : 'transparent')};

	@media ${devices.mobileL} {
		background-color: transparent;
	}
`;

interface ListItemContentProps {
	student: StudentDto;
	search: string;
}

export const ListItemContent: React.FC<ListItemContentProps> = ({ student, search }) => (
	<ListItemLink to={`${ROUTES.APP.STUDENT_CARD}/${student.id}`}>
		<Highlighter
			highlightClassName="search-highlighter"
			searchWords={[search]}
			autoEscape
			textToHighlight={`${student.studentInfo!.firstName} ${student.studentInfo!.lastName}`}
		/>
		<div>
			{student.studentStudyInfo!.group.map((g, i) => (
				<React.Fragment key={g}>
					<StudentGroup type={g} />
					{i !== student.studentStudyInfo!.group.length - 1 && ', '}
				</React.Fragment>
			))}
		</div>
		<Highlighter
			highlightClassName="search-highlighter"
			searchWords={[search]}
			autoEscape
			textToHighlight={student.registrationNumber}
		/>
		<StudentState state={student.state} />
	</ListItemLink>
);

const ListFooter: React.FC = () => (
	<FooterRow>
		<div>
			<img src="/key-arrow-up.svg" alt="Key ArrowUP" className="border" />
			<img src="/key-arrow-down.svg" alt="Key ArrowDonw" className="border" />
			<p>NAHORU, DOLŮ</p>
		</div>
		<div>
			<img src="/key-enter.svg" alt="Key ENTER" className="border" />
			<p>VYBRAT</p>
		</div>
		<div>
			<img src="/key-esc.svg" alt="Key ESC" />
			<p>ZRUŠIT</p>
		</div>
	</FooterRow>
);

interface Props {
	isSearchPage?: boolean;
}

const UserSearch = ({ isSearchPage }: Props) => {
	const isTablet = useIsTablet();
	const navigate = useNavigate();
	const { activeSchoolId } = useActiveSchoolId();
	const [searchInput, setSearchInput] = useState('');
	const [lastSearchDisplayed, setLastSearchDisplayed] = useState(false);
	const [debouncedValue] = useDebounce(searchInput, 200);
	const reportUserSearch = trpc.student.reportUserSearch.useMutation();

	const displayLastSearch = lastSearchDisplayed || (isSearchPage && isTablet);

	const { data, isLoading: isLoadingData } = trpc.student.students.useQuery(
		{
			search: {
				schoolId: activeSchoolId,
				fullText: debouncedValue,
				pagination: { pageSize: 10, currentPage: 1 },
			},
		},
		{
			enabled: debouncedValue.length !== 0,
		},
	);

	const outsideRef = useHandleClickOutside(() => {
		setSearchInput('');
		setLastSearchDisplayed(false);
	});

	const [showStudents, setShowStudents] = useState(false);
	const [displayedStudents, setDisplayedStudents] = useState<any[]>([]);
	const [focusedIndex, setFocusedIndex] = useState<number>(0);

	const handleStudentSelect = (student: StudentDto): void => {
		setTimeout(async () => {
			await reportUserSearch.mutateAsync({
				studentId: student.id,
				schoolId: activeSchoolId,
			});
		}, 1);
		setShowStudents(false);
		setSearchInput('');
		navigate(`${ROUTES.APP.STUDENT_CARD}/${student.id}`);
	};

	const downHandler = useCallback(
		(e: any): void => {
			const { key } = e;
			const resultsLength = (data ?? []).length;

			// disabling keyboard shortcuts when no students are displayed
			if (!showStudents) {
				return;
			}

			switch (key) {
				case 'ArrowUp':
					setFocusedIndex(prevIndex => mod(prevIndex - 1, resultsLength));
					break;
				case 'ArrowDown':
					setFocusedIndex(prevIndex => mod(prevIndex + 1, resultsLength));
					break;
				case 'Enter':
					handleStudentSelect(displayedStudents[focusedIndex]);
					break;
				case 'Escape':
					setShowStudents(false);
					setSearchInput('');
					break;
				default:
					break;
			}
		},
		[displayedStudents, focusedIndex, data],
	);

	useEffect(() => {
		searchInput ? setShowStudents(true) : setShowStudents(false);
		!searchInput && setDisplayedStudents([]);
	}, [searchInput]);

	useEffect(() => {
		if (data) {
			const d = data.slice(0, 5);
			setDisplayedStudents(d);
		}
	}, [data]);

	useEffect(() => {
		window.addEventListener('keydown', downHandler);
		return () => {
			window.removeEventListener('keydown', downHandler);
		};
	}, [downHandler]);

	const renderListContent = () => {
		if (displayLastSearch && !searchInput) {
			return <LastUserSearch handleStudentSelect={handleStudentSelect} />;
		}

		if (displayedStudents.length === 0) {
			return (
				<List
					style={{ backgroundColor: 'white' }}
					locale={{
						emptyText: (
							<>
								<EmptyData />
								<EmptyText>Žáka kterého hledáte není v systému</EmptyText>
							</>
						),
					}}
					bordered
					footer={isTablet ? null : <ListFooter />}
				/>
			);
		}

		return (
			<List
				style={{ backgroundColor: 'white' }}
				bordered
				dataSource={displayedStudents}
				footer={isTablet ? null : <ListFooter />}
				renderItem={(student, index) => (
					<ListItem
						$active={focusedIndex === index}
						onClick={() => handleStudentSelect(student)}
						onMouseEnter={() => setFocusedIndex(index)}
						data-testid={`user-search-list-item-${index}`}
					>
						{searchInput && <ListItemContent student={student} search={searchInput} />}
					</ListItem>
				)}
			/>
		);
	};

	const isLoading = (isLoadingData || debouncedValue !== searchInput) && searchInput.length !== 0;

	return (
		<Wrapper $isTablet={isTablet} data-testid="user-search-wrapper" ref={outsideRef as any}>
			<UserInputSearch
				isLoading={isLoading}
				opened={showStudents}
				onChange={v => {
					if (lastSearchDisplayed) {
						setLastSearchDisplayed(false);
					}

					setSearchInput(v);
					setFocusedIndex(0);
				}}
				onCancel={() => setSearchInput('')}
				onFocus={() => setLastSearchDisplayed(true)}
				value={searchInput}
			/>
			{(showStudents || displayLastSearch) && <div className="results">{renderListContent()}</div>}
		</Wrapper>
	);
};
export default UserSearch;
